import { request } from './request';
import { bind } from '@react-rxjs/core';
import { convertToAsyncTaskState } from '../utils/rxjs/task';
import { pluck, startWith, switchMap, filter, tap } from 'rxjs/operators';
import { catchError, iif, NEVER, throwError } from 'rxjs';
import { queueNotice } from '../components/Notice';
import { setReserveDialog } from '../components/ReserveModal';
import { vehicle$ } from './vehicle';
import { createSignal } from '@react-rxjs/utils';
import { MalfunctionItem } from '../types/malfunction';

const malfunctions$ = vehicle$.pipe(
  switchMap((vehicle) =>
    request<{ items: MalfunctionItem[] }>(
      `/gateway/malfunction-service/v20231215/malfunctions/find`,
      {
        method: 'POST',
        body: JSON.stringify({
          objectIds: [vehicle!.id],
          malfunctionStatuses: ['MALFUNCTION_NEW', 'MALFUNCTION_FIXING'],
        }),
        headers: { 'Content-Type': 'application/json' },
      }
    )
  )
);

export const [refreshMalfunctions$, refreshMalfunctions] = createSignal();

export const [useMalfunctions] = bind(
  refreshMalfunctions$.pipe(
    startWith(null),
    switchMap(() =>
      malfunctions$.pipe(
        pluck('items'),
        catchError((e) => {
          queueNotice({
            message: 'Malfunctions.Get.Error',
          });
          setReserveDialog(false);
          return throwError(() => e);
        }),
        convertToAsyncTaskState()
      )
    )
  ),
  {
    result: null,
    running: true,
    error: null,
  }
);

export const [useLatestMalfunctions] = bind(
  (count: number) =>
    vehicle$.pipe(
      filter(Boolean),
      switchMap(({ id }) =>
        request<{ items: MalfunctionItem[] }>(
          `/gateway/malfunction-service/v20231215/malfunctions/notfixed/${id}`,
          {
            method: 'POST',
            body: JSON.stringify({ page: 0, size: count }),
            headers: { 'Content-Type': 'application/json' },
          }
        ).pipe(
          convertToAsyncTaskState(),
          tap(({ error }) => {
            if (error) {
              queueNotice({
                message: 'Malfunctions.Get.Error',
              });
            }
          })
        )
      )
    ),
  {
    result: null,
    running: true,
    error: null,
  }
);

export const [useMalfunction] = bind(
  (id: string | undefined) =>
    iif(
      () => !!id,
      request<MalfunctionItem>(
        `/gateway/malfunction-service/v20231215/malfunctions/${id}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      ).pipe(
        catchError((e) => {
          queueNotice({
            message: 'Malfunction.Get.Error',
          });

          return throwError(() => e);
        }),
        convertToAsyncTaskState()
      ),
      NEVER
    ),
  {
    result: null,
    running: true,
    error: null,
  }
);
